import React, { useContext, useEffect, useMemo, useState } from "react";
import { DashboardContext } from "./DashboardContext";
import { IClientData, IClientFormData, ISession, ITask } from "../../types/dashboard";
import { AppContext } from "../../appContext";
import { deleteClient, postClient, putClient } from "../../api/api";

import { loadClients as _loadClients } from "../../api/loadClients";
import { deleteSession, getSessions, postSession, putSession } from "../../api/sessionApi";

// Helper function to generate multiple sessions based on repeat parameters
const generateSessionsFromRepeat = (sessionData: ISession): ISession[] => {
    const sessions: ISession[] = [];

    // If no repeat options, return single session
    if (!sessionData.repeat || sessionData.repeat <= 1) {
        return [{ ...sessionData, repeat: undefined, repeatEvery: undefined, repeatInterval: undefined }];
    }

    const baseDate = new Date(sessionData.date);
    const baseDateTo = new Date(sessionData.dateTo);
    const repeatCount = sessionData.repeat;
    const repeatEvery = sessionData.repeatEvery || 1;
    const repeatInterval = sessionData.repeatInterval || 'day';

    for (let i = 0; i < repeatCount; i++) {
        const sessionDate = new Date(baseDate);
        const sessionDateTo = new Date(baseDateTo);

        // Calculate offset based on repeat interval
        let offsetDays = 0;
        switch (repeatInterval) {
            case 'day':
                offsetDays = i * repeatEvery;
                break;
            case 'week':
                offsetDays = i * repeatEvery * 7;
                break;
            case 'month':
                // For months, we use setMonth instead of days
                sessionDate.setMonth(sessionDate.getMonth() + (i * repeatEvery));
                sessionDateTo.setMonth(sessionDateTo.getMonth() + (i * repeatEvery));
                break;
        }

        if (repeatInterval !== 'month') {
            sessionDate.setDate(sessionDate.getDate() + offsetDays);
            sessionDateTo.setDate(sessionDateTo.getDate() + offsetDays);
        }

        // Create session without repeat parameters (since we're expanding them)
        sessions.push({
            ...sessionData,
            date: sessionDate.toISOString(),
            dateTo: sessionDateTo.toISOString(),
            repeat: undefined,
            repeatEvery: undefined,
            repeatInterval: undefined
        });
    }

    return sessions;
};

import { clearScheduleCache } from "./components/scheduleTab/ScheduleTab";
import { clearClientSessionCache } from "./components/sessions/fullSessionList/FullSessionList";
import { BottomMenuItems } from "../../mocks/dashboard";

interface IDashboardControllerProps extends React.PropsWithChildren{

}

import { saveNavigationState, loadNavigationState, NavigationState } from "../../utils/navigationStatePersistence";

export const DashboardController = ({children}: IDashboardControllerProps) => {
    // Load saved state and use it as initial values, with better fallback handling
    const savedState = loadNavigationState();

    const [selectedClientId, setSelectedClientId] = useState<string>(savedState.selectedClientId ?? null);
    const [editClientId, setEditClientId] = useState<string>(savedState.editClientId ?? null);
    const [selectedTaskIndex, setSelectedTaskIndex] = useState(savedState.selectedTaskIndex ?? 1);
    const [selectedSessionIndex, setSelectedSessionIndex] = useState(savedState.selectedSessionIndex ?? 0);
    const [selectedFolderIndex, setSelectedFolderIndex] = useState(savedState.selectedFolderIndex ?? 0);
    const [selectedBottomMenuTabId, setSelectedBottomMenuTabId] = useState<BottomMenuItems>(savedState.selectedBottomMenuTabId as BottomMenuItems ?? '');
    const [selectedLeftMenuTabIndex, setSelectedLeftMenuTabIndex] = useState(savedState.selectedLeftMenuTabIndex ?? 0);
    const [activeTag, setActiveTag] = useState(savedState.activeTag ?? '');
    const [isDashWrap, setDashWrap] = useState(savedState.isDashWrap ?? false);
    const [isLeftCollapsed, setLeftCollapsed] = useState(savedState.isLeftCollapsed ?? false);
    const [showMenu, setShowMenu] = useState(savedState.showMenu ?? false);
    const [activePopup, setActivePopup] = useState(savedState.activePopup ?? '');
    const [sideSelected, setSideSelected] = useState(savedState.sideSelected ?? '');
    const [bottomSelected, _setBottomSelected] = useState<string>(savedState.bottomSelected ?? '');
    const [isDashboardVisible, setIsDashboardVisible] = useState(false); // Always start folded, never restore from savedState
    
    const setBottomSelected = (value: string | ((prev: string) => string)) => {
        _setBottomSelected(value);
    };
    
    const [searchValue, setSearchValue] = useState(savedState.searchValue ?? '');
    const [isInitialized, setIsInitialized] = useState(false);

    const contextData = useContext(AppContext);
    const isClientsLoaded = !!contextData.clients;
    const clientsData = contextData.clients || [];
    const setClientsData = contextData.setClientsRaw;
    const setSessionsRaw = contextData.setSessionsRaw;
    const loadClients = contextData.loadClients;
    const loadSessions = contextData.loadSessions;
    const sessionsRaw = contextData.sessionsRaw;

    // Initialize state from localStorage on component mount
    useEffect(() => {
        if (!isInitialized) {
            const currentSavedState = loadNavigationState();
            if (Object.keys(currentSavedState).length > 0) {
                // Only restore if we have saved state
                if (currentSavedState.selectedClientId !== undefined) setSelectedClientId(currentSavedState.selectedClientId);
                if (currentSavedState.editClientId !== undefined) setEditClientId(currentSavedState.editClientId);
                if (currentSavedState.selectedTaskIndex !== undefined) setSelectedTaskIndex(currentSavedState.selectedTaskIndex);
                if (currentSavedState.selectedSessionIndex !== undefined) setSelectedSessionIndex(currentSavedState.selectedSessionIndex);
                if (currentSavedState.selectedFolderIndex !== undefined) setSelectedFolderIndex(currentSavedState.selectedFolderIndex);
                if (currentSavedState.selectedBottomMenuTabId !== undefined) setSelectedBottomMenuTabId(currentSavedState.selectedBottomMenuTabId as BottomMenuItems);
                if (currentSavedState.selectedLeftMenuTabIndex !== undefined) setSelectedLeftMenuTabIndex(currentSavedState.selectedLeftMenuTabIndex);
                if (currentSavedState.activeTag !== undefined) setActiveTag(currentSavedState.activeTag);
                if (currentSavedState.isDashWrap !== undefined) setDashWrap(currentSavedState.isDashWrap);
                if (currentSavedState.isLeftCollapsed !== undefined) setLeftCollapsed(currentSavedState.isLeftCollapsed);
                if (currentSavedState.showMenu !== undefined) setShowMenu(currentSavedState.showMenu);
                if (currentSavedState.activePopup !== undefined) setActivePopup(currentSavedState.activePopup);
                if (currentSavedState.sideSelected !== undefined) setSideSelected(currentSavedState.sideSelected);
                if (currentSavedState.bottomSelected !== undefined) setBottomSelected(currentSavedState.bottomSelected);
                // Dashboard should always start folded, don't restore old value
                // if (currentSavedState.isDashboardVisible !== undefined) setIsDashboardVisible(currentSavedState.isDashboardVisible);
                if (currentSavedState.searchValue !== undefined) setSearchValue(currentSavedState.searchValue);
            }
            setIsInitialized(true);
        }
    }, [isInitialized]);

    // Save navigation state to localStorage whenever it changes, but only after initialization
    useEffect(() => {
        if (!isInitialized) return; // Don't save during initialization

        const navigationState: NavigationState = {
            selectedClientId,
            editClientId,
            selectedTaskIndex,
            selectedSessionIndex,
            selectedFolderIndex,
            selectedBottomMenuTabId,
            selectedLeftMenuTabIndex,
            activeTag,
            isDashWrap,
            isLeftCollapsed,
            showMenu,
            activePopup,
            sideSelected,
            bottomSelected,
            isDashboardVisible,
            searchValue
        };

        saveNavigationState(navigationState);
    }, [
        isInitialized,
        selectedClientId,
        editClientId,
        selectedTaskIndex,
        selectedSessionIndex,
        selectedFolderIndex,
        selectedBottomMenuTabId,
        selectedLeftMenuTabIndex,
        activeTag,
        isDashWrap,
        isLeftCollapsed,
        showMenu,
        activePopup,
        sideSelected,
        bottomSelected,
        isDashboardVisible,
        searchValue
    ]);

    // Navigation-aware polling for Processing sessions
    useEffect(()=>{
        const processingClientIds: Array<string> = [];
        Object.keys(sessionsRaw).forEach(clientId=>{
            const sessions = sessionsRaw[clientId];
            if (sessions) {
                const processing = sessions.filter(session=> session?.Status === 'Processing');
                if (processing.length){
                    processingClientIds.push(clientId);
                }
            }
        });
        
        // Only poll if:
        // 1. There are processing sessions AND
        // 2. User is viewing session-related content
        const isViewingSessionContent = 
            selectedBottomMenuTabId === 'Sessions' || 
            activePopup === 'FullSessionList' ||
            activePopup === 'AddSession' ||
            activePopup === 'EditSession' ||
            (selectedClientId && processingClientIds.includes(selectedClientId));
        
        if (processingClientIds.length && isViewingSessionContent){
            const timerId = setTimeout(()=>{
                // Load only first, anyway all will be processed step by step
                loadSessions(processingClientIds[0]);
                console.log('reload session for wait... status (navigation-aware polling)');
            }, 10000);
            return ()=>{
                clearTimeout(timerId);
            }
        } else {
            if (processingClientIds.length) {
                console.log('processing sessions exist but user not viewing session content - polling paused');
            } else {
                console.log('no processing sessions');
            }
        }
    }, [sessionsRaw, selectedBottomMenuTabId, activePopup, selectedClientId, loadSessions]);

    const filteredClients = useMemo(()=>{
        if (!clientsData){
            return [];
        }
        if (!searchValue){
            return clientsData
        }
        return clientsData.filter(it=>{
            return it.name.toLowerCase().search(searchValue.toLowerCase()) !=-1;
        });
    }, [clientsData, searchValue])

    const getClientById = (id: string)=>{
        return clientsData.find(client => client.id == id);
    }

    const selectedClient = getClientById(selectedClientId);

    useEffect(()=>{
        if (!selectedClient){
            return
        }
        if (!selectedClient.sessions){
            loadSessions(selectedClient.id);
        }
    }, [selectedClient?.id]); // Only depend on client ID, not the entire object or loadSessions function
    /*const loadClients = ()=>{
        _loadClients().then(result => {
            setClientsData(result);
        })
    } */

    const setTaskList = (setter: (last: ITask[])=>ITask[])=>{
        setClientsData(lastClients=>{
            const selected = selectedClient;
            selected.tasks = setter(selected.tasks);
            return [...lastClients]
        });
    }

    const setSessionData = (setter: (last: ISession[])=>ISession[])=>{
        const clientId = selectedClient.id;
        setSessionsRaw(lastSessions=>{
            const selected = {...lastSessions};
            selected[clientId] = setter(selected[clientId]) as any;
            return selected
        });
    }

    const addClient = (data: IClientFormData) => {
        setClientsData(last=>{
            const next = [...last];
            const nextClient:IClientData = {
                waitLoading: true,
                name: data.firstName + ' ' + data.lastName,
                firstName: data.firstName,
                lastName: data.lastName,

                gender: data.gender,
                email: data.email,
                dateOfBirth: data.dateOfBirth,
                notes: data.notes,
                scheduleDay: data.scheduleDay,

                id: 'temp'+ Date.now(),
                avatarUrl: null,
                tasks: [],
                sessions: [],
                folders: [],  
            };
            next.push(nextClient);
            return next;
        });
        return postClient(data).then((res)=>res.json()).then(res=>{
            console.log(res);
            return loadClients();
        });
    }

    const editClient = (data:IClientFormData)=>{
        putClient(data.id, data).then(()=>{
            setActivePopup('');
            setClientsData(last=>{
                const editIndex = last.findIndex(it=>it.id == data.id);
                const next = [...last];
                const nextClient = {...next[editIndex]};
                nextClient.waitLoading = true;
                next[editIndex] = nextClient;
                return next;
            });
            loadClients(); 
            setEditClientId(null); 
        })
    }

    const _deleteClient=(id: string)=>{
        deleteClient(id).then(()=>{
            setActivePopup('');
            setClientsData(last=>{
                const editIndex = last.findIndex(it=>it.id == id);
                const next = [...last];
                const nextClient = {...next[editIndex]};
                nextClient.waitLoading = true;
                next[editIndex] = nextClient;
                return next;
            });
            loadClients(); 
            setEditClientId(null);
        });
    }

    const editSession = (sessionData: ISession) => {
        const currentClientId = selectedClient.id;

        if (sessionData.id){
            // Update local state immediately
            setSessionData((last)=>{
                const next = last.map(it=> it.id == sessionData.id ? {...sessionData, waitLoading: true} : it);
                return next
            });

            putSession(sessionData, sessionData.id).then((response)=>{
                if (response.ok) {
                    // Update local session and remove loading state
                    const updatedSession = { ...sessionData, waitLoading: false };
                    setSessionData((last)=>{
                        const next = last.map(it=> it.id == sessionData.id ? updatedSession : it);
                        return next
                    });
                } else {
                    // If failed, remove loading state and show error
                    setSessionData((last)=>{
                        const next = last.map(it=> it.id == sessionData.id ? {...sessionData, waitLoading: false} : it);
                        return next
                    });
                }
            });
        } else {
            if (sessionData.client){
            // Generate multiple sessions if repeat options are specified
            const sessionsToCreate = generateSessionsFromRepeat(sessionData);

            // Add temp sessions with loading state immediately for optimistic UI
            const tempSessions = sessionsToCreate.map((session, index) => ({
                ...session,
                id: 'temp_' + Date.now() + '_' + index,
                waitLoading: true
            }));

            setSessionData((last)=>{
                const next = [...last, ...tempSessions];
                return next
            });

            // Create sessions on server (send them one by one)
            sessionsToCreate.forEach((session, index) => {
                const tempId = tempSessions[index].id;

                postSession(session).then((response)=>{
                    if (response.ok) {
                        return response.json();
                    } else {
                        throw new Error('Failed to create session');
                    }
                }).then((result) => {
                    // Handle both single ID and array of IDs from server
                    let createdId;
                    if (result.created_id) {
                        // If server returns an array, use the first available ID
                        if (Array.isArray(result.created_id)) {
                            createdId = result.created_id[0];
                        } else {
                            createdId = result.created_id;
                        }
                    } else {
                        // Fallback to old format
                        createdId = result.id || result.sessionId;
                    }

                    console.log('Replacing temp session', tempId, 'with real ID', createdId);
                    console.log('Session data to replace:', session);

                    // Replace temp session with real session using server-returned ID
                    const sessionWithId = { ...session, id: createdId, waitLoading: false };
                    setSessionData((last)=>{
                        console.log('Current sessions before replacement:', last.map(s => ({ id: s.id, title: s.title })));
                        const tempSessionIndex = last.findIndex(it => it.id === tempId);
                        console.log('Temp session index:', tempSessionIndex);

                        if (tempSessionIndex === -1) {
                            console.error('Temp session not found!', tempId);
                            return last;
                        }

                        const next = [...last];
                        next[tempSessionIndex] = sessionWithId;
                        console.log('Sessions after replacement:', next.map(s => ({ id: s.id, title: s.title })));
                        return next;
                    });
                }).catch((error) => {
                    console.error(`Failed to create session ${index + 1}:`, error);
                    // If failed, remove temp session
                    setSessionData((last)=>{
                        const next = last.filter(it=> it.id !== tempId);
                        return next;
                    });
                });
            });
        }
        }
    }

    const _deleteSession = (sessionData: ISession) => {
        const currentClientId = selectedClient.id;

        // Show loading state immediately
        setSessionData((last)=>{
            const next = last.map(it=> it.id == sessionData.id ? {...sessionData, waitLoading: true} : it);
            return next
        });

        deleteSession(sessionData.id).then((response)=>{
            if (response.ok) {
                // Remove session from local state only after successful server response
                setSessionData((last)=>{
                    const next = last.filter(it=> it.id !== sessionData.id);
                    return next
                });
            } else {
                // If failed, remove loading state
                setSessionData((last)=>{
                    const next = last.map(it=> it.id == sessionData.id ? {...sessionData, waitLoading: false} : it);
                    return next
                });
            }
        }).catch(() => {
            // If failed, remove loading state
            setSessionData((last)=>{
                const next = last.map(it=> it.id == sessionData.id ? {...sessionData, waitLoading: false} : it);
                return next
            });
        });
    }

    const applySearch = (value: string)=>{
        setSearchValue(value);
    }

    return (
        <DashboardContext.Provider value={{
            //raw data
            selectedClientId, setSelectedClientId,
            editClientId, setEditClientId,
            selectedTaskIndex, setSelectedTaskIndex,
            selectedSessionIndex, setSelectedSessionIndex,
            selectedFolderIndex, setSelectedFolderIndex,
            selectedBottomMenuTabId, setSelectedBottomMenuTabId,
            selectedLeftMenuTabIndex, setSelectedLeftMenuTabIndex,
            activeTag, setActiveTag,
            sideSelected, setSideSelected, 
            isDashWrap, setDashWrap,
            isLeftCollapsed, setLeftCollapsed,
            showMenu, setShowMenu,
            activePopup,
            setActivePopup,
            selectedClient,
            setTaskList,
            bottomSelected,
            setBottomSelected,
            isDashboardVisible,
            setIsDashboardVisible,

            //methods
            addClient,
            editClient,
            deleteClient: _deleteClient,
            editSession,
            deleteSession: _deleteSession,
            setSelectedSessionForEdit: (session: ISession) => {
                // Переключаемся на вкладку Sessions и открываем редактирование сессии
                setSelectedBottomMenuTabId('Sessions');
                setActivePopup('FullSessionList');

                // Используем setTimeout для обеспечения правильного порядка обновления состояния
                setTimeout(() => {
                    setActivePopup('AddSession');
                }, 50);
            },
            applySearch,
            filteredClients,
            searchValue
        }}>
            {children}
        </DashboardContext.Provider>
    )
}